import { z } from 'zod'
import { FirestoreTimestamp } from './utils'

/**
 * Oil Record
 *
 * Tracks oil additions to elevator units including gallons added,
 * reason for addition, and who completed the work.
 */

// Base oil record schema with all fields
const BaseOilRecordSchema = z.object({
  // Core oil record data
  date: z.iso.date(),
  gallonsAdded: z.number().nonnegative('Gallons added must be non-negative'),
  reason: z.string().min(1, 'Reason is required'),

  // Media and documentation
  photos: z.array(z.string()).default([]),

  // Technician information
  completedBy: z.string().min(1, 'Completed by is required'),
  completedByUserId: z.string().optional(), // Optional link to actual user document
})

// Full oil record schema (for database documents)
export const OilRecord = BaseOilRecordSchema.extend({
  unitId: z.string(),
  createdAt: FirestoreTimestamp,
  updatedAt: FirestoreTimestamp,
})

// Create oil record schema - requires all fields except auto-generated ones
export const CreateOilRecordSchema = BaseOilRecordSchema.extend({
  // Required fields for creation
  unitId: z.string().min(1, 'Unit ID is required'),
  siteId: z.string().min(1, 'Site ID is required'),
  date: z.iso.date(),
}).partial({
  // Make optional fields explicitly partial (will be auto-populated from context)
  photos: true,
  completedBy: true,
  completedByUserId: true,
})

// Update oil record schema - only siteId, unitId, and oilRecordId are required
export const UpdateOilRecordSchema = CreateOilRecordSchema.extend({
  oilRecordId: z.string().min(1, 'Oil Record ID is required'),
}).partial({
  // Make all data fields optional for updates, keeping only identifiers required
  date: true,
  gallonsAdded: true,
  reason: true,
})

export type OilRecord = z.infer<typeof OilRecord>
export type CreateOilRecordData = z.infer<typeof CreateOilRecordSchema>
export type UpdateOilRecordData = z.infer<typeof UpdateOilRecordSchema>
